import Modal, {
  Appearance,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import React, { FC, useCallback, useMemo } from 'react';
import { translationKeys, useTranslation } from 'services/localization';
import { selectConsentDialog, selectItem } from '../../preferencesSelectors';
import { sendItemsFromUI, showConsentDialog } from '../../preferencesSlice';
import {
  trackAcceptConsentClick,
  trackDeclineConsentClick,
  trackSubscriptionGroupUpdate,
  trackSubscriptionUpdate,
} from '../../services/userActionEvents';
import { TriState } from '../../types';
import Button from '@atlaskit/button';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { ItemStatus } from '../../../../services/api';

const ConsentDialog: FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation();
  const dialogPayload = useAppSelector(selectConsentDialog);
  const currentGmoi = useAppSelector(selectItem('consents', 'generalMarketingOptIn'));

  const updateKeys: string[] = useMemo(() => [], []);
  let message = '';
  if (dialogPayload.update?.sourceGroup === 'product_recommendations') {
    if (currentGmoi !== ItemStatus.ON) {
      message = translate(translationKeys.CONSENT_DIALOG_GMOI_MESSAGE);
      updateKeys.push('generalMarketingOptIn');
    }
  } else if (dialogPayload.update?.sourceGroup === 'consents') {
    if (dialogPayload.update.sourceItem === 'generalMarketingOptIn') {
      message = translate(translationKeys.CONSENT_DIALOG_GMOI_MESSAGE);
      updateKeys.push('generalMarketingOptIn');
    } else if (dialogPayload.update.sourceItem === undefined) {
      if (dialogPayload.update.keys.length === 1 && dialogPayload.update.keys[0] === 'generalMarketingOptIn') {
        message = translate(translationKeys.CONSENT_DIALOG_GMOI_MESSAGE);
      }
      for (const item of dialogPayload.update.keys) {
        updateKeys.push(item);
      }
    }
  }

  const acceptHandler = useCallback(async () => {
    const sourceItem = dialogPayload.update?.sourceItem;
    const sourceGroup = dialogPayload.update?.sourceGroup;
    const sourceTab = dialogPayload.update?.sourceTab;

    trackAcceptConsentClick({
      source: sourceItem,
      groupName: sourceGroup,
      componentName: 'modal',
    });

    if (sourceGroup === 'product_recommendations') {
      if (sourceItem) {
        trackSubscriptionUpdate(true, { source: sourceItem });
      } else {
        trackSubscriptionGroupUpdate(true, { groupName: sourceGroup, tabName: sourceTab });
      }
    }

    // accept consents and originating request
    const keys: string[] = [];
    keys.push(...updateKeys);
    if (dialogPayload.update !== undefined && dialogPayload.update !== null) {
      keys.push(...dialogPayload.update.keys);
    }

    dispatch(
      sendItemsFromUI({
        keys: keys,
        state: TriState.true,
        bypassConsentCheck: false,
        bypassConsentDeclinePrCheck: false,
        skipNotification: false,
        sourceGroup: dialogPayload.update?.sourceGroup,
        sourceItem: dialogPayload.update?.sourceItem,
      }),
    );

    // close dialog
    dispatch(showConsentDialog({ showDialog: false }));
  }, [dispatch, dialogPayload, updateKeys]);

  const cancelHandler = useCallback(() => {
    trackDeclineConsentClick({
      source: dialogPayload.update?.sourceItem,
      groupName: dialogPayload.update?.sourceGroup,
      componentName: 'modal',
    });

    // close dialog
    dispatch(showConsentDialog({ showDialog: false }));
  }, [dispatch, dialogPayload]);

  const actions = [
    {
      text: translate(translationKeys.CONSENT_DIALOG_ACCEPT_BUTTON_LABEL),
      onClick: acceptHandler,
      autoFocus: true,
      appearance: 'primary' as Appearance,
      testId: 'consent-dialog-accept-button',
    },
    {
      text: translate(translationKeys.CONSENT_DIALOG_DECLINE_BUTTON_LABEL),
      onClick: cancelHandler,
      appearance: 'subtle' as Appearance,
      testId: 'consent-dialog-cancel-button',
    },
  ];

  return (
    <ModalTransition>
      {dialogPayload.showDialog && (
        <Modal onClose={cancelHandler} testId='consentDialog'>
          <ModalHeader>
            <ModalTitle>{translate(translationKeys.CONSENT_DIALOG_LABEL)}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p dangerouslySetInnerHTML={{ __html: message }} />
          </ModalBody>
          <ModalFooter>
            {actions.map((a) => (
              <Button
                key={a.testId}
                appearance={a.appearance}
                onClick={a.onClick}
                autoFocus={a.autoFocus}
                testId={a.testId}
              >
                {a.text}
              </Button>
            ))}
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};

export default ConsentDialog;
